import { Injectable } from '@angular/core';
import { Subject, timer, Observable, forkJoin, Subscription } from 'rxjs';
import { SessionTimerEndWarningDialogComponent } from '../components/session-timer-end-warning-dialog/session-timer-end-warning-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ShopSettingsService } from '../modules/general/services/shop-settings.service';
import { LoggerService } from './logger/logger.service';
import { LoadingSpinnerService } from './loading-spinner.service';
import { LoginStateService } from '../modules/login/services/login-state.service';
import { OrderStateService } from '../modules/order/services/order-state.service';

@Injectable()
export class InactiveTimerService {
  endCount = new Subject();
  subscription: any;
  secondsDisplay: number;
  minutesDisplay: number;
  currentTimerValue: number;
  dialogIsShow: boolean;
  timerIsStarted: boolean;
  private stopTimerActions = new Map<string, () => Observable<any>>();

  private logoutSubscription: Subscription;

  constructor(
    public dialog: MatDialog,
    private router: Router,
    private shopSettingsService: ShopSettingsService,
    private loggerService: LoggerService,
    private loadingSpinnerService: LoadingSpinnerService,
    private loginStateService: LoginStateService,
    private orderStateService: OrderStateService,
  ) {
    this.timerIsStarted = false;
    this.dialogIsShow = false;
  }

  get orderChangedTimeout(): number {
    return this.orderStateService.order?.isChanged ?  this.shopSettingsService.shopSettings?.orderSessionTimeOut || 0 : 0;
  }

  getSessionTimeout(): number {
    let sessionTimeout = this.shopSettingsService.shopSettings && this.shopSettingsService.shopSettings.sessionTimeOut
      ? this.shopSettingsService.shopSettings.sessionTimeOut : 0;
    if (
      (
        (
          this.shopSettingsService.isVUMode
          && this.shopSettingsService.shopSettings
          && this.shopSettingsService.shopSettings.partnerCodeAuthorization
        )
        || (!this.shopSettingsService.isVUMode)
      )
      && this.loginStateService.isLoggedIn) {
      sessionTimeout = this.shopSettingsService.shopSettings && this.shopSettingsService.shopSettings.sessionTimeOutLoggedIn ?
        this.shopSettingsService.shopSettings.sessionTimeOutLoggedIn : 0;
    }

    return sessionTimeout;
  }

  initInactiveTimer(): void {
    const endTime = this.getSessionTimeout() || this.orderChangedTimeout;
    const interval = 1000;
    const duration = endTime * 60;

    if (duration === 0) {
      return null;
    }

    this.clearTimer();
    const sessionTimeoutCountdown = this.shopSettingsService.shopSettings && this.shopSettingsService.shopSettings.sessionTimeOutCountdown ?
      this.shopSettingsService.shopSettings.sessionTimeOutCountdown : 60;

    this.subscription = timer(0, 1000)
      .subscribe(
        value => {
          if (value > (duration - sessionTimeoutCountdown) && !this.dialogIsShow) {
            this.dialogIsShow = true;
            this.showSessionTimerEndWarning();
          }
          if (value === duration + 1) {
            this.dialog.closeAll();
            this.executeAllActionsAndRedirectToMainPage();
          } else {
            this.currentTimerValue = value;
            this.timerIsStarted = true;

            this.render((duration - +value) * interval);
          }
        },
        err => { },
        () => {
          this.endCount.next();
        });
  }

  private executeAllActionsAndRedirectToMainPage(): void {
    try {
      if (this.getSessionTimeout() === 0 && this.orderChangedTimeout !== 0) {
        this.navigateToMainPage();
        return;
      }

      if (this.stopTimerActions.size === 0) {
        this.navigateToMainPage();
        return;
      }

      this.loadingSpinnerService.show();
      forkJoin(Array.from(this.stopTimerActions.values()).map(f => f())).subscribe(_ => {
        this.navigateToMainPage();
        this.loadingSpinnerService.hide();
      }, (error) => {
        this.loggerService.error(error);
        this.navigateToMainPage();
        this.loadingSpinnerService.hide();
      });
    } catch (e) {
      this.loggerService.error(e);
    }
  }

  private navigateToMainPage(): void {
    if (!this.shopSettingsService.isVUMode && this.shopSettingsService.shopSettings.logoutByTimeout) {
      this.unsubscribeLogoutSubscription();
      this.loadingSpinnerService.show();
      this.logoutSubscription = this.loginStateService.logout().subscribe(_ => {
        this.unsubscribeLogoutSubscription();
        this.router.navigateByUrl('');
      });
      return;
    }

    this.router.navigateByUrl('');
  }

  unsubscribeLogoutSubscription(): void {
    this.loadingSpinnerService.hide();
    if (this.logoutSubscription) {
      this.logoutSubscription.unsubscribe();
      this.logoutSubscription = null;
    }
  }

  private render(count): void {
    this.secondsDisplay = this.getSeconds(count);
    this.minutesDisplay = this.getMinutes(count);
  }

  private getSeconds(ticks: number): number {
    const seconds = ((ticks % 60000) / 1000).toFixed(0);
    return this.pad(seconds);
  }

  private getMinutes(ticks: number): number {
    const minutes = Math.floor(ticks / 60000);
    return this.pad(minutes);
  }

  private pad(digit: any): number {
    return digit <= 9 ? '0' + digit : digit;
  }

  resetTimer(): void {
    this.stopTimerActions.clear();
    this.clearTimer();
    this.initInactiveTimer();
  }

  clearTimer(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
    if (this.dialogIsShow) {
      this.dialogIsShow = false;
    }
    if (this.timerIsStarted) {
      this.timerIsStarted = false;
    }
    if (this.currentTimerValue) {
      this.currentTimerValue = 0;
    }
  }

  showSessionTimerEndWarning(): void {
    const dialogRef = this.dialog.open(SessionTimerEndWarningDialogComponent, { id: 'sessionTimerEndWarningDialog' });
    dialogRef.afterClosed().subscribe(() => {
      if (dialogRef.componentInstance.continueTimer) {
        this.resetTimer();
      }
    });
  }

  registerStopTimerAction(name: string, action: () => Observable<any>): void {
    this.stopTimerActions.set(name, action);
  }

  unregisterStopTimerAction(name: string): void {
    this.stopTimerActions.delete(name);
  }

  resetAndCloseSessionTimerEndWarningDialog(): void {
    if (!this.subscription) {
      return;
    }

    const dialogRef = this.dialog.getDialogById('sessionTimerEndWarningDialog');
    if (dialogRef) {
      dialogRef.close();
      this.resetTimer();
    } else {
      this.resetTimer();
    }
  }
}
